/**
 * Sharing point-in-time secret with another user: envelope encrypted to their
 * public key (ECIES). Alternative to P2P-QR when recipient is not present.
 *
 * Sender only needs recipient's PUBLIC key (published by server).
 * Server forwards opaque envelope; cannot read it. Optionally envelope
 * expires (plaintext metadata for server to purge, but content
 * remains unreadable to it).
 */

import { openWithPrivateKey, type SealedToPublicKey, sealToPublicKey } from "./asymmetric";
import { utf8Decode, utf8Encode } from "./encoding";
import type { VaultItemPlain } from "./types";

const AAD_SHARE = "boveda:shared-secret:v1";

export interface SharedSecret {
  readonly sealed: SealedToPublicKey;
  readonly fromUserId: string;
  readonly toUserId: string;
  /** Optional ISO; server can purge after this date (non-sensitive metadata). */
  readonly expiresAt?: string;
}

export async function shareSecret(
  fromUserId: string,
  toUserId: string,
  recipientPublicKey: string,
  secret: VaultItemPlain,
  expiresAt?: string,
): Promise<SharedSecret> {
  const sealed = await sealToPublicKey(
    recipientPublicKey,
    utf8Encode(JSON.stringify(secret)),
    AAD_SHARE,
  );
  return { sealed, fromUserId, toUserId, expiresAt };
}

export async function openSharedSecret(
  recipientPrivateKey: string,
  shared: SharedSecret,
): Promise<VaultItemPlain> {
  const bytes = await openWithPrivateKey(recipientPrivateKey, shared.sealed, AAD_SHARE);
  // Trust boundary: envelope was sealed by sender (AEAD already authenticated content).
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(bytes)) as VaultItemPlain;
}
